import { useState, useEffect } from "react";
import { where, orderBy, limit, startAfter } from "firebase/firestore";
import { getFirestoreService } from "../services/firestoreService";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from "recharts";

const ROLE_COLORS = {
  admin: "#1e40af",
  supervisor: "#059669",
  geopatrulheiro: "#7c3aed",
  unknown: "#6b7280",
};

const MESES_RANK = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

// Data de hoje em Brasília (YYYY-MM-DD) — replicado do App.js por ser função
// pura e o App.js não exportá-la. Usado como valor padrão do filtro de data
// da aba Escritas.
const getDataBrasiliaISO = () => {
  const formatter = new Intl.DateTimeFormat('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Sao_Paulo' });
  const partes = formatter.formatToParts(new Date());
  const ano = partes.find(p => p.type === 'year').value;
  const mes = partes.find(p => p.type === 'month').value;
  const dia = partes.find(p => p.type === 'day').value;
  return `${ano}-${mes}-${dia}`;
};

// Mesmo padrão de identificação do App (posto RE nome). Replicado aqui por ser
// função pura e o App.js não exportá-la.
const formatarNomeComPosto = (nome, rank, badge) => {
  const n = (nome || "").trim(); if (!n) return "—";
  const r = (rank || "").trim(); const b = (badge || "").toString().trim();
  return [r, b, n].filter(Boolean).join(" ");
};

export default function AnalyticsDashboard({ user, unitLabel, generatePDF }) {
  const [analytics, setAnalytics] = useState([]);
  const [consultasPublicas, setConsultasPublicas] = useState([]);
  const [ipsBlockeados, setIpsBlockeados] = useState([]);
  const [firestoreMetrics, setFirestoreMetrics] = useState(null);
  // Agregação cross-sessão de snapshots de métricas (dados REAIS de leituras
  // do Firestore, persistidos por FirestoreManager.saveMetricsSnapshot).
  // Substitui as estimativas do ESTIMATED_READS por contagens reais.
  const [crossSessionSnaps, setCrossSessionSnaps] = useState([]); // docs brutos
  const [crossSessionLoading, setCrossSessionLoading] = useState(false);
  // Filtros do painel de Diagnóstico (aplicados sobre snapshots brutos)
  const [diagJanela, setDiagJanela] = useState("24h"); // 1h, 6h, 12h, 24h, 7d, hoje
  const [diagUsuario, setDiagUsuario] = useState("");
  const [diagColecao, setDiagColecao] = useState("");
  const [diagOrigem, setDiagOrigem] = useState("");
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroAcao, setFiltroAcao] = useState("todos");
  const [filtroIP, setFiltroIP] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [periodo, setPeriodo] = useState("hoje");
  const [loading, setLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState("");
  const [diagError, setDiagError] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("read");
  const [expandidoAcoes, setExpandidoAcoes] = useState(false);
  // Ranking de RIT por usuário (produtividade mensal)
  const [rankingRits, setRankingRits] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankMes, setRankMes] = useState(new Date().getMonth());
  const [rankAno, setRankAno] = useState(new Date().getFullYear());
  // RITs gerados/editados no dia (aba Escritas) — filtro por data, padrão hoje
  const [dataEscrita, setDataEscrita] = useState(getDataBrasiliaISO());
  const [ritsEscrita, setRitsEscrita] = useState([]);
  const [ritsEscritaLoading, setRitsEscritaLoading] = useState(false);
  // Análise Individual — RIT criados por 1 usuário (nome ou RE) num mês de
  // referência. Só busca quando os dois parâmetros estiverem preenchidos e o
  // usuário clicar em "Buscar" (não é reativo a cada tecla/troca de mês).
  const [individualBusca, setIndividualBusca] = useState("");
  const [individualMes, setIndividualMes] = useState(new Date().getMonth());
  const [individualAno, setIndividualAno] = useState(new Date().getFullYear());
  const [individualRits, setIndividualRits] = useState([]);
  const [individualLoading, setIndividualLoading] = useState(false);
  const [individualBuscou, setIndividualBuscou] = useState(false); // já rodou ao menos 1 busca

  useEffect(() => {
    if (abaAtiva === "publica") {
      loadConsultasPublicas();
    } else if (abaAtiva === "firestore") {
      loadFirestoreMetrics();
    } else if (abaAtiva === "ranking") {
      loadRankingRits();
    } else if (abaAtiva === "write") {
      loadRitsEscrita();
    } else if (abaAtiva === "individual") {
      // Nada a carregar ao abrir a aba — a busca só roda no clique de
      // "Buscar" (ver buscarIndividual), depois que nome/RE e mês estejam
      // preenchidos.
    } else {
      loadAnalytics();
    }
  }, [periodo, abaAtiva, rankMes, rankAno]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recarrega ao trocar a data selecionada, sem depender da troca de aba
  useEffect(() => { if (abaAtiva === "write") loadRitsEscrita(); }, [dataEscrita]); // eslint-disable-line react-hooks/exhaustive-deps

  // RITs gerados e/ou editados no dia selecionado (aba Escritas). Duas
  // consultas por intervalo de data (createdAt e updatedAt) — Firestore não
  // permite OR entre campos diferentes numa única query — e depois mescla
  // por id, marcando cada RIT como "Criado" e/ou "Editado" naquele dia. Um
  // RIT recém-criado sempre bate em createdAt e updatedAt (mesmo instante),
  // então só marca "Editado" quando updatedAt cai no dia mas createdAt não
  // — ou seja, houve uma edição de verdade, não só a criação.
  const loadRitsEscrita = async () => {
    if (!dataEscrita) return;
    setRitsEscritaLoading(true);
    try {
      const inicio = new Date(`${dataEscrita}T00:00:00-03:00`);
      const fim = new Date(`${dataEscrita}T23:59:59.999-03:00`);
      const fm = getFirestoreService();
      const [criados, editados] = await Promise.all([
        fm.getDocs("rits", [where("createdAt", ">=", inicio), where("createdAt", "<=", fim)]),
        fm.getDocs("rits", [where("updatedAt", ">=", inicio), where("updatedAt", "<=", fim)]),
      ]);
      const porId = {};
      criados.forEach(r => { porId[r.id] = { ...r, criadoNoDia: true, editadoNoDia: false }; });
      editados.forEach(r => {
        if (porId[r.id]) return; // já é "criado" nesse dia — não duplica como "editado"
        porId[r.id] = { ...r, criadoNoDia: false, editadoNoDia: true };
      });
      const lista = Object.values(porId).sort((a, b) => {
        const ta = (a.updatedAt?.seconds || 0), tb = (b.updatedAt?.seconds || 0);
        return tb - ta;
      });
      setRitsEscrita(lista);
    } catch (e) {
      console.error("Erro ao carregar RITs do dia:", e);
      setRitsEscrita([]);
    }
    setRitsEscritaLoading(false);
  };

  const loadRankingRits = async () => {
    setRankingLoading(true);
    try {
      const inicio = new Date(rankAno, rankMes, 1, 0, 0, 0, 0);
      const fim = new Date(rankAno, rankMes + 1, 1, 0, 0, 0, 0);
      const fm = getFirestoreService();
      const data = await fm.getDocs("rits", [
        where("createdAt", ">=", inicio),
        where("createdAt", "<", fim),
        orderBy("createdAt", "desc")
      ]);
      setRankingRits(data);
    } catch (e) {
      console.error("Erro ao carregar ranking de RIT:", e);
      setRankingRits([]);
    }
    setRankingLoading(false);
  };

  // ── Análise Individual: RIT criados por 1 usuário (nome ou RE) no mês de
  // referência. Consulta por intervalo de createdAt (mesmo padrão do
  // Ranking) e filtra por nome/RE no cliente — evita precisar de um índice
  // composto novo. Só roda quando chamada explicitamente pelo botão
  // "Buscar" (nome/RE precisa estar preenchido).
  const buscarIndividual = async () => {
    const termo = individualBusca.trim();
    if (!termo) return;
    setIndividualLoading(true);
    setIndividualBuscou(true);
    try {
      const inicio = new Date(individualAno, individualMes, 1, 0, 0, 0, 0);
      const fim = new Date(individualAno, individualMes + 1, 1, 0, 0, 0, 0);
      const fm = getFirestoreService();
      const data = await fm.getDocs("rits", [
        where("createdAt", ">=", inicio),
        where("createdAt", "<", fim),
        orderBy("createdAt", "desc")
      ]);
      const termoLower = termo.toLowerCase();
      const filtrados = data.filter(r => {
        const autor = efetivoElaborador(r);
        const nome = autor.nome.toLowerCase();
        const re = autor.badge.toLowerCase();
        return nome.includes(termoLower) || re === termoLower;
      });
      setIndividualRits(filtrados);
    } catch (e) {
      console.error("Erro ao buscar RITs por usuário:", e);
      setIndividualRits([]);
    }
    setIndividualLoading(false);
  };

  const loadFirestoreMetrics = () => {
    setLoading(true);
    try {
      const fm = getFirestoreService();
      const metrics = fm.getDetailedMetrics();
      setFirestoreMetrics(metrics);
    } catch (e) {
      console.error("Erro ao carregar métricas do Firestore:", e);
    }
    setLoading(false);
    // Carrega também a agregação cross-sessão em background — barato
    // (1 query, tipicamente <100 docs de snapshots por dia).
    loadCrossSessionAgg();
  };

  // Carrega snapshots crus dos últimos 7d — os filtros no cliente re-agregam
  // sem novo fetch. 7d de todos os usuários costuma dar centenas de snapshots
  // (não milhares), então buscar de uma vez é OK.
  const loadCrossSessionAgg = async () => {
    setCrossSessionLoading(true);
    try {
      const fm = getFirestoreService();
      const desde = new Date(Date.now() - 7 * 24 * 3600 * 1000);
      const snaps = await fm.getDocs("user_analytics", [
        where("action", "==", "firestore_metrics_snapshot"),
        where("timestamp", ">=", desde),
        orderBy("timestamp", "desc"),
      ]);
      setCrossSessionSnaps(snaps);
      setDiagError("");
    } catch (e) {
      console.error("Erro ao carregar snapshots de métricas:", e);
      setCrossSessionSnaps([]);
      setDiagError("Não foi possível carregar os snapshots. Verifique o índice ou as permissões.");
    }
    setCrossSessionLoading(false);
  };

  // Agregação com filtros aplicados. Recalcula em memória a cada mudança de
  // filtro, sem re-fetch. Contas são baratas — no máximo ~500 snapshots.
  const diagAgg = (() => {
    if (!crossSessionSnaps || crossSessionSnaps.length === 0) return null;
    // Janela de tempo
    const agora = Date.now();
    const janelas = { "1h": 3600, "6h": 21600, "12h": 43200, "24h": 86400, "7d": 604800 };
    let desdeSec;
    if (diagJanela === "hoje") {
      const d = new Date(); d.setHours(0,0,0,0); desdeSec = d.getTime() / 1000;
    } else {
      desdeSec = agora / 1000 - (janelas[diagJanela] || 86400);
    }
    // Filtros de substring (case-insensitive)
    const fu = diagUsuario.toLowerCase().trim();
    const fc = diagColecao.toLowerCase().trim();
    const fo = diagOrigem.toLowerCase().trim();
    const snapsFiltrados = crossSessionSnaps.filter(s => {
      const ts = s.timestamp?.seconds || (s.timestamp instanceof Date ? s.timestamp.getTime() / 1000 : 0);
      if (ts < desdeSec) return false;
      if (fu && !(s.userName || "").toLowerCase().includes(fu)) return false;
      return true;
    });
    // Timeline por hora (bucket de 3600s)
    const hourly = {};
    // Agregações
    const porOrigem = {};
    const porColecao = {};
    const porUsuario = {};
    let totalLeituras = 0, totalEscritas = 0, totalHits = 0, totalMisses = 0, totalSaved = 0, docsObservados = 0, comMedicao = 0;
    snapsFiltrados.forEach(s => {
      const ts = s.timestamp?.seconds || (s.timestamp instanceof Date ? s.timestamp.getTime() / 1000 : 0);
      const hourBucket = Math.floor(ts / 3600) * 3600;
      const hits = s.stats?.hits || 0;
      const misses = s.stats?.misses || 0;
      const writes = s.stats?.writes || 0;
      const saved = s.summary?.totalSavedReads || 0;
      totalHits += hits; totalMisses += misses; totalEscritas += writes; totalSaved += saved;
      if (Number.isFinite(s.stats?.observedDocs)) { docsObservados += s.stats.observedDocs; comMedicao++; }
      totalLeituras += misses;
      hourly[hourBucket] = (hourly[hourBucket] || 0) + misses;
      // Por origem
      Object.entries(s.byOrigin || {}).forEach(([origem, d]) => {
        if (fo && !origem.toLowerCase().includes(fo)) return;
        // Se filtro de coleção ativo, só considera origens que tocaram a coleção
        if (fc && !(d.collections || []).some(c => c.toLowerCase().includes(fc))) return;
        if (!porOrigem[origem]) porOrigem[origem] = { count: 0, colecoes: new Set(), usuarios: new Set() };
        porOrigem[origem].count += d.count || 0;
        (d.collections || []).forEach(c => porOrigem[origem].colecoes.add(c));
        if (s.userName) porOrigem[origem].usuarios.add(s.userName);
      });
      // Por coleção
      Object.entries(s.byCollection || {}).forEach(([col, d]) => {
        if (fc && !col.toLowerCase().includes(fc)) return;
        if (!porColecao[col]) porColecao[col] = { count: 0, savedReads: 0, errors: 0 };
        porColecao[col].count += d.count || 0;
        porColecao[col].savedReads += d.savedReads || 0;
        porColecao[col].errors += d.errors || 0;
      });
      // Por usuário
      if (s.userName) {
        if (!porUsuario[s.userName]) porUsuario[s.userName] = { count: 0, colecoes: new Set(), role: s.userRole, unidade: s.userUnit };
        porUsuario[s.userName].count += (s.summary?.totalOperations || 0);
        Object.keys(s.byCollection || {}).forEach(c => porUsuario[s.userName].colecoes.add(c));
      }
    });
    // Converte Sets → arrays
    Object.values(porOrigem).forEach(v => { v.colecoes = Array.from(v.colecoes); v.usuarios = Array.from(v.usuarios); });
    Object.values(porUsuario).forEach(v => { v.colecoes = Array.from(v.colecoes); });
    // Timeline como array ordenado
    const timelineArr = Object.entries(hourly)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([sec, count]) => ({ hora: new Date(Number(sec) * 1000).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) + " " + new Date(Number(sec) * 1000).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }), leituras: count }));
    const hitRate = totalHits + totalMisses > 0 ? (totalHits / (totalHits + totalMisses)) * 100 : 0;
    // Custo real Firestore: US$0.06 por 100k reads, US$0.18 por 100k writes
    const custoLeituraUS = (totalLeituras / 100000) * 0.06;
    const custoEscritaUS = (totalEscritas / 100000) * 0.18;
    const custoTotalUS = custoLeituraUS + custoEscritaUS;
    // Alertas: outliers óbvios
    const alertas = [];
    Object.entries(porUsuario).forEach(([nome, d]) => {
      if (d.count > 5000) alertas.push({ tipo: "user", texto: `Usuário ${nome} (${d.role}) — ${d.count.toLocaleString()} leituras no período` });
    });
    Object.entries(porOrigem).forEach(([o, d]) => {
      if (d.count > 2000) alertas.push({ tipo: "origem", texto: `Origem ${o} disparou ${d.count.toLocaleString()} leituras (${d.usuarios.length} usuário(s))` });
    });
    Object.entries(porColecao).forEach(([c, d]) => {
      if (d.count > 5000) alertas.push({ tipo: "colecao", texto: `Coleção /${c} recebeu ${d.count.toLocaleString()} leituras` });
    });
    return {
      snapsFiltrados: snapsFiltrados.length,
      porOrigem, porColecao, porUsuario,
      timeline: timelineArr,
      totalLeituras, totalEscritas, totalHits, totalMisses, totalSaved, docsObservados, comMedicao,
      hitRate,
      custoTotalUS, custoLeituraUS, custoEscritaUS,
      alertas,
      usuariosAtivos: Object.keys(porUsuario).length,
      origensUnicas: Object.keys(porOrigem).length,
      colecoesUnicas: Object.keys(porColecao).length,
    };
  })();

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const dataInicio = getDataInicio(periodo);
      const fm = getFirestoreService();
      const data = await fm.getDocs("user_analytics", [
        where("timestamp", ">=", dataInicio),
        orderBy("timestamp", "desc")
      ]);
      setAnalytics(data);
      setAnalyticsError("");
    } catch (e) {
      setAnalytics([]);
      setAnalyticsError("Não foi possível carregar os acessos deste período.");
      console.error("Erro ao carregar analytics:", e);
    }
    setLoading(false);
  };

  const loadConsultasPublicas = async () => {
    setLoading(true);
    try {
      const dataInicio = getDataInicio(periodo);
      const fm = getFirestoreService();
      const data = await fm.getDocs("consultas_publicas_log", [
        where("timestamp", ">=", dataInicio),
        orderBy("timestamp", "desc")
      ]);
      setConsultasPublicas(data);

      const bloqueados = await fm.getDocs("ips_bloqueados", []);
      setIpsBlockeados(bloqueados);
    } catch (e) {
      console.error("Erro ao carregar consultas públicas:", e);
    }
    setLoading(false);
  };

  const getDataInicio = (tipo) => {
    const hoje = getDataBrasiliaISO();
    if (tipo === "mes") return new Date(`${hoje.slice(0, 7)}-01T00:00:00-03:00`);
    if (tipo === "semana") {
      const [ano, mes, dia] = hoje.split("-").map(Number);
      const data = new Date(Date.UTC(ano, mes - 1, dia));
      data.setUTCDate(data.getUTCDate() - 6);
      return new Date(`${data.toISOString().slice(0, 10)}T00:00:00-03:00`);
    }
    if (tipo === "hoje") return new Date(`${hoje}T00:00:00-03:00`);
    return new Date(0);
  };

  // Filtros para Reads/Writes
  const dados = analytics
    .filter(a => a.type === abaAtiva)
    .filter(a => !filtroUsuario || (a.userName || "").toLowerCase().includes(filtroUsuario.toLowerCase()))
    .filter(a => filtroAcao === "todos" || a.action === filtroAcao);

  // Filtros para Consultas Públicas
  const consultasFiltradas = consultasPublicas
    .filter(c => !filtroIP || (c.ip || "").includes(filtroIP))
    .filter(c => filtroStatus === "todos" || (
      filtroStatus === "sucesso" ? c.sucesso :
      filtroStatus === "bloqueado" ? (c.motivo || "").includes("bloqueado") :
      filtroStatus === "rate-limit" ? (c.motivo || "").includes("Limite") :
      (c.motivo || "").includes("inválido")
    ));

  const acessosPorUsuario = Object.values(analytics.reduce((map, a) => {
    if (a.type !== "access" && a.type !== "read" && a.action !== "firestore_metrics_snapshot") return map;
    const key = a.userId || a.userName || "desconhecido";
    if (!map[key]) map[key] = { key, nome: a.userName || "Desconhecido", logins: 0, acoes: 0, estimativa: 0, docsObservados: 0, possuiMedicao: false };
    if (a.type === "access" && a.action === "login") map[key].logins++;
    if (a.type === "read") { map[key].acoes++; map[key].estimativa += Number(a.estimatedReads) || 0; }
    if (a.action === "firestore_metrics_snapshot" && Number.isFinite(a.stats?.observedDocs)) {
      map[key].docsObservados += a.stats.observedDocs;
      map[key].possuiMedicao = true;
    }
    return map;
  }, {})).filter(x => !filtroUsuario || x.nome.toLowerCase().includes(filtroUsuario.toLowerCase())).sort((a,b) => b.logins - a.logins || b.docsObservados - a.docsObservados);

  // Agregações para Reads/Writes
  const totalReads = dados.reduce((sum, a) => sum + (a.estimatedReads || 0), 0);
  const porUsuario = {};
  const porAcao = {};

  dados.forEach(a => {
    porUsuario[a.userName] = (porUsuario[a.userName] || 0) + (a.estimatedReads || 0);
    porAcao[a.action] = (porAcao[a.action] || 0) + (a.estimatedReads || 0);
  });

  const usuariosDados = Object.entries(porUsuario)
    .map(([nome, reads]) => ({ nome, reads }))
    .sort((a, b) => b.reads - a.reads)
    .slice(0, 10);

  const acoesDados = Object.entries(porAcao)
    .map(([acao, reads]) => ({ acao, reads }))
    .sort((a, b) => b.reads - a.reads);

  const CORES_ACOES = [
    "#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"
  ];

  const porRole = {};
  dados.forEach(a => {
    porRole[a.userRole] = (porRole[a.userRole] || 0) + (a.estimatedReads || 0);
  });

  const roleDados = Object.entries(porRole)
    .map(([role, reads]) => ({ name: role, value: reads }));

  // Agregações para Consultas Públicas
  const totalTentativas = consultasFiltradas.length;
  const tentativasBemsucedidas = consultasFiltradas.filter(c => c.sucesso).length;
  const tentativasFalhadas = totalTentativas - tentativasBemsucedidas;
  const porIP = {};
  const porMotivo = {};

  consultasFiltradas.forEach(c => {
    porIP[c.ip] = (porIP[c.ip] || 0) + 1;
    const motivo = c.motivo || "Desconhecido";
    porMotivo[motivo] = (porMotivo[motivo] || 0) + 1;
  });

  const ipsTopDados = Object.entries(porIP)
    .map(([ip, count]) => ({ ip, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const motivosDados = Object.entries(porMotivo)
    .map(([motivo, count]) => ({ motivo, count }))
    .sort((a, b) => b.count - a.count);

  // ── Ranking de RIT por elaborador (mês selecionado) ────────────────────────
  // Usa o elaborador "efetivo": se houver substituição de admin (elaboradoPor*),
  // ela prevalece; senão, o operador que criou o RIT. Agrupa por RE (badge)
  // quando existir, senão pelo nome.
  const efetivoElaborador = (r) => {
    const nome = (r.elaboradoPorNome && r.elaboradoPorNome.trim()) || r.operatorName || "—";
    const rank = r.elaboradoPorRank || r.operatorRank || "";
    const badge = (r.elaboradoPorBadge && r.elaboradoPorBadge.toString().trim()) || (r.badge || "").toString().trim();
    return { key: badge || nome, nome, rank, badge, unitCode: r.unitCode };
  };
  const rankAgg = {};
  rankingRits.forEach(r => {
    const e = efetivoElaborador(r);
    if (!rankAgg[e.key]) rankAgg[e.key] = { ...e, count: 0 };
    rankAgg[e.key].count++;
  });
  const ranking = Object.values(rankAgg).sort((a, b) => b.count - a.count);
  const anosRank = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
  const medalha = (i) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}º`);

  return (
    <div style={{ padding: "20px", background: "#f9fafb", minHeight: "100vh" }}>
      {/* Cabeçalho */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
        background: "#fff",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
      }}>
        <div>
          <div style={{ fontSize: "24px", fontWeight: "800", color: "#1f2937", marginBottom: "4px" }}>
            📊 Analytics do Sistema
          </div>
          <div style={{ fontSize: "13px", color: "#6b7280" }}>
            {abaAtiva === "read" ? "Entradas por usuário e estimativas de consumo por atividade registrada." : abaAtiva === "write" ? "RIT gerados e editados no dia selecionado (query real na coleção rits)" : abaAtiva === "ranking" ? "Ranking de produtividade: quantos RIT cada usuário gerou no mês — base para a campanha de valorização" : abaAtiva === "individual" ? "RIT criados por um usuário específico (nome ou RE) em um mês de referência" : abaAtiva === "firestore" ? "🔬 Consultas monitoradas pelo FirestoreManager, com contagem parcial de documentos retornados" : "Monitore acessos anônimos e tentativas de consulta pública"}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            onClick={() => setAbaAtiva("read")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "read" ? "2px solid #3b82f6" : "1px solid #e5e7eb",
              background: abaAtiva === "read" ? "#eff6ff" : "#fff",
              color: abaAtiva === "read" ? "#1e40af" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            🔑 Entradas por usuário
          </button>
          <button
            onClick={() => setAbaAtiva("write")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "write" ? "2px solid #10b981" : "1px solid #e5e7eb",
              background: abaAtiva === "write" ? "#f0fdf4" : "#fff",
              color: abaAtiva === "write" ? "#059669" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            ✏️ Escritas
          </button>
          <button
            onClick={() => setAbaAtiva("firestore")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "firestore" ? "2px solid #8b5cf6" : "1px solid #e5e7eb",
              background: abaAtiva === "firestore" ? "#faf5ff" : "#fff",
              color: abaAtiva === "firestore" ? "#6d28d9" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            🔬 Diagnóstico Firestore
          </button>
          <button
            onClick={() => setAbaAtiva("publica")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "publica" ? "2px solid #f59e0b" : "1px solid #e5e7eb",
              background: abaAtiva === "publica" ? "#fffbeb" : "#fff",
              color: abaAtiva === "publica" ? "#b45309" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            🌐 Visualização Pública
          </button>
          <button
            onClick={() => setAbaAtiva("ranking")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "ranking" ? "2px solid #b45309" : "1px solid #e5e7eb",
              background: abaAtiva === "ranking" ? "#fffbeb" : "#fff",
              color: abaAtiva === "ranking" ? "#b45309" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            🏆 Ranking de RIT
          </button>
          <button
            onClick={() => setAbaAtiva("individual")}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: abaAtiva === "individual" ? "2px solid #1e40af" : "1px solid #e5e7eb",
              background: abaAtiva === "individual" ? "#eff6ff" : "#fff",
              color: abaAtiva === "individual" ? "#1e40af" : "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            🔎 Análise Individual
          </button>
          {abaAtiva !== "firestore" && abaAtiva !== "ranking" && abaAtiva !== "write" && abaAtiva !== "individual" && ["hoje", "semana", "mes"].map(p => (
            <button
              key={p}
              onClick={() => setPeriodo(p)}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: periodo === p ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                background: periodo === p ? "#eff6ff" : "#fff",
                color: periodo === p ? "#1e40af" : "#6b7280",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {p === "hoje" ? "Hoje" : p === "semana" ? "7 Dias" : "Este mês"}
            </button>
          ))}
        </div>
      </div>

      {/* ===================== ESCRITAS: RIT GERADOS/EDITADOS NO DIA ===================== */}
      {abaAtiva === "write" && (
        <div>
          {/* Filtro de data — padrão hoje, mas pode ser alterado */}
          <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1f2937" }}>Data:</span>
            <input type="date" value={dataEscrita} onChange={e => setDataEscrita(e.target.value)} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px" }} />
            {dataEscrita !== getDataBrasiliaISO() && (
              <button onClick={() => setDataEscrita(getDataBrasiliaISO())} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer", fontWeight: 600, fontSize: "12px" }}>Voltar para hoje</button>
            )}
            <span style={{ marginLeft: "auto", fontSize: "12px", color: "#6b7280" }}>{ritsEscritaLoading ? "Carregando…" : `${ritsEscrita.length} RIT no dia`}</span>
          </div>

          {ritsEscritaLoading ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Carregando…</div>
          ) : ritsEscrita.length === 0 ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Nenhum RIT gerado ou editado em {new Date(dataEscrita + "T00:00:00").toLocaleDateString("pt-BR")}.</div>
          ) : (
            <>
              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #10b981", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#10b981" }}>{ritsEscrita.length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📄 RIT no dia</div>
                </div>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #3b82f6", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#3b82f6" }}>{ritsEscrita.filter(r => r.criadoNoDia).length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>🆕 Gerados</div>
                </div>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #f59e0b", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#f59e0b" }}>{ritsEscrita.filter(r => r.editadoNoDia).length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>✏️ Editados</div>
                </div>
              </div>

              {/* Tabela */}
              <div style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>📋 RIT gerados e editados — {new Date(dataEscrita + "T00:00:00").toLocaleDateString("pt-BR")}</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>RIT</th>
                        <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}>Tipo</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>👤 Usuário</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Unidade</th>
                        <th style={{ padding: "12px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>📎 Anexos</th>
                        <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ritsEscrita.map((r, idx) => (
                        <tr key={r.id} style={{ background: idx % 2 === 0 ? "#f9fafb" : "#fff", borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: "12px", fontWeight: 700, color: "#1f2937" }}>{r.numero}</td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, background: r.criadoNoDia ? "rgba(59,130,246,0.1)" : "rgba(245,158,11,0.15)", color: r.criadoNoDia ? "#1e40af" : "#92400e" }}>
                              {r.criadoNoDia ? "Gerado" : "Editado"}
                            </span>
                          </td>
                          <td style={{ padding: "12px", color: "#1f2937" }}>{formatarNomeComPosto(r.operatorName, r.operatorRank, r.badge)}</td>
                          <td style={{ padding: "12px", color: "#6b7280" }}>{unitLabel ? unitLabel(r.unitCode) : (r.unitCode || "—")}</td>
                          <td style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#1f2937" }}>{(r.imagens?.length || 0) + (r.pdfs?.length || 0)}</td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            {generatePDF && (
                              <button onClick={() => generatePDF(r)} title="Ver RIT em PDF" style={{ padding: "5px 12px", borderRadius: "6px", border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.1)", color: "#dc2626", cursor: "pointer", fontWeight: 700, fontSize: "11px" }}>
                                📄 PDF
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===================== RANKING DE RIT POR USUÁRIO ===================== */}
      {abaAtiva === "ranking" && (
        <div>
          {/* Filtro mês/ano */}
          <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1f2937" }}>Mês de referência:</span>
            <select value={rankMes} onChange={e => setRankMes(Number(e.target.value))} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px", cursor: "pointer" }}>
              {MESES_RANK.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select value={rankAno} onChange={e => setRankAno(Number(e.target.value))} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px", cursor: "pointer" }}>
              {anosRank.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <span style={{ marginLeft: "auto", fontSize: "12px", color: "#6b7280" }}>{rankingLoading ? "Carregando…" : `${rankingRits.length} RIT no período · ${ranking.length} elaborador(es)`}</span>
          </div>

          {rankingLoading ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Carregando ranking…</div>
          ) : ranking.length === 0 ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Nenhum RIT elaborado em {MESES_RANK[rankMes]}/{rankAno}.</div>
          ) : (
            <>
              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #b45309", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#b45309" }}>{rankingRits.length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📄 RIT no mês</div>
                </div>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #f59e0b", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#f59e0b" }}>{ranking.length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>👥 Elaboradores</div>
                </div>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #10b981", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#10b981" }}>{formatarNomeComPosto(ranking[0].nome, ranking[0].rank, ranking[0].badge)}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>🏆 Líder do mês ({ranking[0].count} RIT)</div>
                </div>
              </div>

              {/* Tabela completa */}
              <div style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>📋 Ranking completo — {MESES_RANK[rankMes]}/{rankAno}</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600", width: "70px" }}>Posição</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>👤 Elaborador</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Unidade</th>
                        <th style={{ padding: "12px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>📄 RIT</th>
                        <th style={{ padding: "12px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>% do mês</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.map((x, idx) => (
                        <tr key={x.key} style={{ background: idx % 2 === 0 ? "#f9fafb" : "#fff", borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: "12px", textAlign: "center", fontWeight: 800, fontSize: idx < 3 ? "18px" : "13px", color: "#b45309" }}>{medalha(idx)}</td>
                          <td style={{ padding: "12px", color: "#1f2937", fontWeight: idx < 3 ? 700 : 500 }}>{formatarNomeComPosto(x.nome, x.rank, x.badge)}</td>
                          <td style={{ padding: "12px", color: "#6b7280" }}>{unitLabel ? unitLabel(x.unitCode) : (x.unitCode || "—")}</td>
                          <td style={{ padding: "12px", textAlign: "right", fontWeight: 700, color: "#b45309" }}>{x.count}</td>
                          <td style={{ padding: "12px", textAlign: "right", color: "#6b7280" }}>{rankingRits.length > 0 ? ((x.count / rankingRits.length) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===================== ANÁLISE INDIVIDUAL: RIT POR USUÁRIO (NOME/RE) NO MÊS ===================== */}
      {abaAtiva === "individual" && (
        <div>
          {/* Filtros — busca só roda ao clicar em Buscar, nunca sozinha */}
          <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <input
              type="text"
              placeholder="Nome ou RE do usuário..."
              value={individualBusca}
              onChange={e => setIndividualBusca(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") buscarIndividual(); }}
              style={{ flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px" }}
            />
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1f2937" }}>Mês de referência:</span>
            <select value={individualMes} onChange={e => setIndividualMes(Number(e.target.value))} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px", cursor: "pointer" }}>
              {MESES_RANK.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select value={individualAno} onChange={e => setIndividualAno(Number(e.target.value))} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", fontSize: "13px", cursor: "pointer" }}>
              {anosRank.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button
              onClick={buscarIndividual}
              disabled={!individualBusca.trim() || individualLoading}
              style={{ padding: "8px 18px", borderRadius: "6px", border: "none", background: individualBusca.trim() ? "#1e40af" : "#9ca3af", color: "#fff", cursor: individualBusca.trim() ? "pointer" : "not-allowed", fontWeight: 700, fontSize: "12px" }}
            >
              {individualLoading ? "Buscando…" : "🔎 Buscar"}
            </button>
          </div>

          {!individualBuscou ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Informe o nome ou RE do usuário e o mês de referência, depois clique em Buscar.</div>
          ) : individualLoading ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Carregando…</div>
          ) : individualRits.length === 0 ? (
            <div style={{ background: "#fff", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#6b7280", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>Nenhum RIT encontrado para "{individualBusca}" em {MESES_RANK[individualMes]}/{individualAno}.</div>
          ) : (
            <>
              {/* KPI */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", borderLeft: "4px solid #1e40af", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "28px", fontWeight: "800", color: "#1e40af" }}>{individualRits.length}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📄 RIT no período</div>
                </div>
              </div>

              {/* Tabela */}
              <div style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>📋 RIT criados por "{individualBusca}" — {MESES_RANK[individualMes]}/{individualAno}</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>RIT</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Data</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>👤 Usuário</th>
                        <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Unidade</th>
                        <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}>Status</th>
                        <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {individualRits.map((r, idx) => (
                        <tr key={r.id} style={{ background: idx % 2 === 0 ? "#f9fafb" : "#fff", borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: "12px", fontWeight: 700, color: "#1f2937" }}>{r.numero}</td>
                          <td style={{ padding: "12px", color: "#6b7280" }}>{r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000).toLocaleDateString("pt-BR") : "—"}</td>
                          <td style={{ padding: "12px", color: "#1f2937" }}>{formatarNomeComPosto(r.operatorName, r.operatorRank, r.badge)}</td>
                          <td style={{ padding: "12px", color: "#6b7280" }}>{unitLabel ? unitLabel(r.unitCode) : (r.unitCode || "—")}</td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, background: r.status === "fiscalizado" ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.15)", color: r.status === "fiscalizado" ? "#059669" : "#92400e" }}>
                              {r.status === "fiscalizado" ? "Fiscalizado" : "Pendente"}
                            </span>
                          </td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            {generatePDF && (
                              <button onClick={() => generatePDF(r)} title="Ver RIT em PDF" style={{ padding: "5px 12px", borderRadius: "6px", border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.1)", color: "#dc2626", cursor: "pointer", fontWeight: 700, fontSize: "11px" }}>
                                📄 PDF
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {abaAtiva === "read" && <section style={{background:"#fff",padding:20,borderRadius:8,marginBottom:20}}>
        <h2 style={{marginTop:0}}>Entradas registradas por usuário</h2>
        <p>Entradas são logins concluídos com senha. A medição de documentos começa após esta atualização e é gravada periodicamente. Conta documentos retornados pelas consultas do FirestoreManager; não inclui consultas diretas, listeners, eventuais cobranças mínimas ou sessões ainda não sincronizadas. Não equivale à cobrança do Firebase.</p>
        <p>{loading ? "Carregando…" : `${acessosPorUsuario.reduce((sum,x)=>sum+x.logins,0)} entradas · ${acessosPorUsuario.length} usuários com atividade`}</p>
        {analyticsError && <p role="alert" style={{color:"#991b1b"}}>{analyticsError}</p>}
        <button type="button" onClick={() => { getFirestoreService().invalidateCache("user_analytics"); loadAnalytics(); }} disabled={loading} style={{padding:"7px 12px",marginBottom:12,marginRight:12}}>Atualizar dados</button>
        <input aria-label="Buscar usuário" placeholder="Buscar usuário..." value={filtroUsuario} onChange={e=>setFiltroUsuario(e.target.value)} style={{padding:8,marginBottom:12,width:"100%",maxWidth:320}} />
        <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",textAlign:"left"}}><thead><tr><th>Usuário</th><th>Entradas</th><th>Ações registradas</th><th>Documentos observados</th><th>Estimativa anterior por ações</th></tr></thead><tbody>{acessosPorUsuario.map(x=><tr key={x.key} style={{borderTop:"1px solid #e5e7eb"}}><td style={{padding:10}}>{x.nome}</td><td>{x.logins}</td><td>{x.acoes}</td><td>{x.possuiMedicao ? x.docsObservados.toLocaleString("pt-BR") : "Sem medição"}</td><td>{x.estimativa.toLocaleString("pt-BR")}</td></tr>)}</tbody></table></div>
        {!loading && !analyticsError && acessosPorUsuario.length===0 && <p>Nenhum acesso ou atividade registrado no período.</p>}
      </section>}
      {abaAtiva !== "ranking" && abaAtiva !== "write" && abaAtiva !== "individual" && abaAtiva !== "read" && (<>
      {/* Firestore Admin Dashboard */}
      {abaAtiva === "firestore" && firestoreMetrics ? (
        <>
          {/* Botão de atualizar — os dados mostrados aqui são acumulados na
              memória do navegador desde o login do usuário. Sem este botão,
              o Snapshot fica congelado no momento em que a aba foi aberta. */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <button onClick={loadFirestoreMetrics} style={{ padding: "6px 14px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "#fff", color: "#1f2937", cursor: "pointer", fontWeight: 600, fontSize: "12px" }}>
              🔄 Atualizar métricas desta sessão
            </button>
          </div>
          {/* KPIs Firestore */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "24px" }}>
            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #3b82f6",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#3b82f6" }}>{firestoreMetrics.summary.totalRequests}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📊 Consultas Executadas</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #10b981",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#10b981" }}>{firestoreMetrics.stats.hits}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>💾 Cache Hit</div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{firestoreMetrics.stats.hitRate}</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #ef4444",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#ef4444" }}>{firestoreMetrics.stats.misses}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>❌ Cache Miss</div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{firestoreMetrics.stats.missRate}</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #f59e0b",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#f59e0b" }}>{firestoreMetrics.summary.totalSavedReads.toLocaleString()}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>✨ Leituras Evitadas</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #8b5cf6",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#8b5cf6" }}>{firestoreMetrics.stats.dedupedRequests}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>🔄 Queries Duplicadas</div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{firestoreMetrics.stats.dedupRate}</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #06b6d4",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#06b6d4" }}>{firestoreMetrics.summary.overallReduction}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📉 Economia %</div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #ec4899",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "24px", fontWeight: "800", color: "#ec4899" }}>{(firestoreMetrics.cache.totalBytes / 1024).toFixed(1)} KB</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>💾 Tamanho do Cache</div>
            </div>
          </div>

          {/* Coleções mais consultadas */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              📋 Coleções Mais Consultadas
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr",
              gap: "12px",
              marginBottom: "12px",
              fontWeight: "600",
              fontSize: "12px",
              color: "#6b7280",
              padding: "8px 12px",
              background: "#f3f4f6",
              borderRadius: "4px"
            }}>
              <div>Coleção</div>
              <div style={{ textAlign: "right" }}>Consultas</div>
              <div style={{ textAlign: "right" }}>Tempo Médio</div>
              <div style={{ textAlign: "right" }}>Erros</div>
              <div style={{ textAlign: "right" }}>Leituras Economizadas</div>
            </div>
            {Object.entries(firestoreMetrics.operationsByCollection)
              .sort(([, a], [, b]) => b.count - a.count)
              .slice(0, 10)
              .map(([coll, data], idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr",
                    gap: "12px",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f3f4f6",
                    alignItems: "center",
                    fontSize: "13px"
                  }}
                >
                  <div style={{ fontWeight: "600", color: "#1f2937" }}>{coll}</div>
                  <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: "500", color: "#3b82f6" }}>
                    {data.count}
                  </div>
                  <div style={{ textAlign: "right", color: "#6b7280" }}>
                    {(data.avgDuration || 0).toFixed(0)}ms
                  </div>
                  <div style={{ textAlign: "right", color: data.errors > 0 ? "#ef4444" : "#6b7280" }}>
                    {data.errors}
                  </div>
                  <div style={{ textAlign: "right", color: "#10b981", fontWeight: "600" }}>
                    {(data.savedReads || 0).toLocaleString()}
                  </div>
                </div>
              ))}
          </div>

          {/* Origens das consultas — pinça qual código gerou cada leitura.
              A informação por origem (arquivo:linha) já era coletada pelo
              FirestoreManager desde sempre; só faltava esta visualização.
              Útil para diagnosticar de onde vêm leituras inesperadas
              (ex.: se "App.js:XXXX (useAutorAoVivo)" aparecer no topo com
              milhares de chamadas, é o hook lendo /users demais). */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              🎯 Origens das Consultas (esta sessão)
            </div>
            <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "8px" }}>
              Cada linha mostra quantas leituras vieram de um mesmo local do código nesta sessão do navegador. Origens no topo com contagens altas são candidatas a otimização.
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "3fr 1fr 2fr",
              gap: "12px",
              marginBottom: "12px",
              fontWeight: "600",
              fontSize: "12px",
              color: "#6b7280",
              padding: "8px 12px",
              background: "#f3f4f6",
              borderRadius: "4px"
            }}>
              <div>Origem (arquivo:linha)</div>
              <div style={{ textAlign: "right" }}>Chamadas</div>
              <div>Coleções tocadas</div>
            </div>
            {Object.entries(firestoreMetrics.operationsByOrigin || {})
              .sort(([, a], [, b]) => b.count - a.count)
              .slice(0, 15)
              .map(([origin, data], idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "3fr 1fr 2fr",
                    gap: "12px",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f3f4f6",
                    alignItems: "center",
                    fontSize: "12px"
                  }}
                >
                  <div style={{ fontWeight: "600", color: "#1f2937", fontFamily: "monospace", overflowWrap: "anywhere" }}>{origin}</div>
                  <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: "700", color: data.count > 20 ? "#dc2626" : data.count > 5 ? "#f59e0b" : "#3b82f6" }}>
                    {data.count}
                  </div>
                  <div style={{ color: "#6b7280", fontSize: "11px" }}>{(data.collections || []).join(", ")}</div>
                </div>
              ))}
            {Object.keys(firestoreMetrics.operationsByOrigin || {}).length === 0 && (
              <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "12px" }}>Nenhuma operação registrada ainda nesta sessão.</div>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              🔬 DIAGNÓSTICO REAL — snapshots persistidos em user_analytics
              Dados REAIS de leituras (não estimativas). Filtros aplicados no
              cliente para não gastar novas leituras a cada mudança.
              ═══════════════════════════════════════════════════════════════ */}
          <div style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "2px solid #1e40af" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "800", color: "#1e40af" }}>🔬 Diagnóstico de Operações Monitoradas</div>
                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>Operações observadas pelo gerenciador; não representam documentos cobrados nem incluem consultas diretas ao Firebase.</div>
              </div>
              <button onClick={loadCrossSessionAgg} style={{ padding: "6px 14px", borderRadius: "6px", border: "1px solid #1e40af", background: "#eff6ff", color: "#1e40af", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>🔄 Recarregar (últimos 7d)</button>
            </div>

            {/* Filtros */}
            <div style={{ background: "#f9fafb", padding: "12px", borderRadius: "6px", marginBottom: "16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>Janela</div>
                <select value={diagJanela} onChange={e => setDiagJanela(e.target.value)} style={{ width: "100%", padding: "6px 10px", borderRadius: "4px", border: "1px solid #e5e7eb", fontSize: "12px" }}>
                  <option value="1h">Última hora</option>
                  <option value="6h">Últimas 6h</option>
                  <option value="12h">Últimas 12h</option>
                  <option value="24h">Últimas 24h</option>
                  <option value="7d">Últimos 7 dias</option>
                  <option value="hoje">Hoje (00:00 →)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>Usuário (contém)</div>
                <input type="text" value={diagUsuario} onChange={e => setDiagUsuario(e.target.value)} placeholder="ex.: DANIEL" style={{ width: "100%", padding: "6px 10px", borderRadius: "4px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
              </div>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>Coleção (contém)</div>
                <input type="text" value={diagColecao} onChange={e => setDiagColecao(e.target.value)} placeholder="ex.: rits, users" style={{ width: "100%", padding: "6px 10px", borderRadius: "4px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
              </div>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>Origem (contém)</div>
                <input type="text" value={diagOrigem} onChange={e => setDiagOrigem(e.target.value)} placeholder="ex.: App.js:13" style={{ width: "100%", padding: "6px 10px", borderRadius: "4px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
              </div>
              {(diagUsuario || diagColecao || diagOrigem) && (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button onClick={() => { setDiagUsuario(""); setDiagColecao(""); setDiagOrigem(""); }} style={{ padding: "6px 12px", borderRadius: "4px", border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer", fontSize: "12px" }}>Limpar filtros</button>
                </div>
              )}
            </div>

            {diagError && <p role="alert" style={{color:"#991b1b"}}>{diagError}</p>}
            {crossSessionLoading ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "12px" }}>Carregando…</div>
            ) : !diagAgg || diagAgg.snapsFiltrados === 0 ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "12px" }}>
                Nenhum snapshot no período/filtros escolhidos.
                {crossSessionSnaps.length === 0 && " Os primeiros snapshots aparecem 15 minutos após o deploy dessa observabilidade, ou no próximo logout dos usuários."}
              </div>
            ) : (
              <>
                {diagAgg.comMedicao > 0 && <p style={{fontSize:13,color:"#1e40af"}}><strong>{diagAgg.docsObservados.toLocaleString("pt-BR")} documentos retornados</strong> em {diagAgg.comMedicao} snapshots com medição. Parcial: não inclui operações fora do gerenciador.</p>}
                {/* KPIs REAIS */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "8px", marginBottom: "16px" }}>
                  <div style={{ background: "#eff6ff", padding: "10px", borderRadius: "6px", border: "1px solid #dbeafe" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#1e40af" }}>{diagAgg.totalLeituras.toLocaleString()}</div>
                    <div style={{ fontSize: "10px", color: "#1e40af", textTransform: "uppercase" }}>📊 Consultas sem cache</div>
                  </div>
                  <div style={{ background: "#fef3c7", padding: "10px", borderRadius: "6px", border: "1px solid #fde68a" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#92400e" }}>{diagAgg.totalEscritas.toLocaleString()}</div>
                    <div style={{ fontSize: "10px", color: "#92400e", textTransform: "uppercase" }}>✏️ Tentativas de escrita</div>
                  </div>
                  <div style={{ background: diagAgg.hitRate < 30 ? "#fee2e2" : diagAgg.hitRate < 60 ? "#fef3c7" : "#dcfce7", padding: "10px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: diagAgg.hitRate < 30 ? "#991b1b" : diagAgg.hitRate < 60 ? "#92400e" : "#166534" }}>{diagAgg.hitRate.toFixed(1)}%</div>
                    <div style={{ fontSize: "10px", color: "#374151", textTransform: "uppercase" }}>💾 Cache hit rate</div>
                  </div>
                  <div style={{ background: "#dcfce7", padding: "10px", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#166534" }}>{diagAgg.totalSaved.toLocaleString()}</div>
                    <div style={{ fontSize: "10px", color: "#166534", textTransform: "uppercase" }}>✨ Economizadas</div>
                  </div>
                  <div style={{ background: "#f3e8ff", padding: "10px", borderRadius: "6px", border: "1px solid #e9d5ff" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#6d28d9" }}>US$ {diagAgg.custoTotalUS.toFixed(4)}</div>
                    <div style={{ fontSize: "10px", color: "#6d28d9", textTransform: "uppercase" }}>💰 Projeção parcial*</div>
                  </div>
                  <div style={{ background: "#f9fafb", padding: "10px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#374151" }}>{diagAgg.usuariosAtivos}</div>
                    <div style={{ fontSize: "10px", color: "#374151", textTransform: "uppercase" }}>👥 Usuários</div>
                  </div>
                  <div style={{ background: "#f9fafb", padding: "10px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#374151" }}>{diagAgg.origensUnicas}</div>
                    <div style={{ fontSize: "10px", color: "#374151", textTransform: "uppercase" }}>🎯 Origens</div>
                  </div>
                  <div style={{ background: "#f9fafb", padding: "10px", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#374151" }}>{diagAgg.snapsFiltrados}</div>
                    <div style={{ fontSize: "10px", color: "#374151", textTransform: "uppercase" }}>📸 Snapshots</div>
                  </div>
                </div>
                <div style={{ fontSize: "10px", color: "#9ca3af", marginBottom: "16px" }}>* Projeção sobre operações monitoradas; não mede documentos cobrados, consultas diretas, cota gratuita ou cobrança efetiva.</div>

                {/* Alertas */}
                {diagAgg.alertas.length > 0 && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", padding: "12px", marginBottom: "16px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#991b1b", marginBottom: "6px" }}>⚠️ Anomalias detectadas ({diagAgg.alertas.length})</div>
                    {diagAgg.alertas.slice(0, 10).map((a, i) => (
                      <div key={i} style={{ fontSize: "11px", color: "#7f1d1d", marginBottom: "3px" }}>• {a.texto}</div>
                    ))}
                  </div>
                )}

                {/* Timeline — reads por hora */}
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>📉 Timeline (leituras por hora)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={diagAgg.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="hora" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="leituras" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>

                {/* Top origens */}
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginTop: "20px", marginBottom: "6px" }}>🎯 Origens que mais consumiram (arquivo:linha do código)</div>
                <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 2fr", gap: "12px", padding: "8px 12px", background: "#f3f4f6", borderRadius: "4px", fontWeight: "600", fontSize: "11px", color: "#6b7280" }}>
                  <div>Origem</div>
                  <div style={{ textAlign: "right" }}>Chamadas</div>
                  <div style={{ textAlign: "right" }}>Usuários</div>
                  <div>Coleções</div>
                </div>
                {Object.entries(diagAgg.porOrigem).sort(([, a], [, b]) => b.count - a.count).slice(0, 20).map(([origem, d], idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 2fr", gap: "12px", padding: "8px 12px", borderBottom: "1px solid #f8fafc", fontSize: "11px" }}>
                    <div style={{ fontFamily: "monospace", color: "#1f2937", overflowWrap: "anywhere" }}>{origem}</div>
                    <div style={{ textAlign: "right", fontWeight: 700, color: d.count > 2000 ? "#dc2626" : d.count > 500 ? "#f59e0b" : "#3b82f6" }}>{d.count.toLocaleString()}</div>
                    <div style={{ textAlign: "right", color: "#6b7280" }}>{d.usuarios.length}</div>
                    <div style={{ color: "#6b7280", overflowWrap: "anywhere" }}>{d.colecoes.join(", ")}</div>
                  </div>
                ))}

                {/* Usuários */}
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginTop: "20px", marginBottom: "6px" }}>👥 Usuários por consumo</div>
                <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 2fr", gap: "12px", padding: "8px 12px", background: "#f3f4f6", borderRadius: "4px", fontWeight: "600", fontSize: "11px", color: "#6b7280" }}>
                  <div>Usuário</div>
                  <div style={{ textAlign: "right" }}>Chamadas</div>
                  <div style={{ textAlign: "right" }}>Perfil</div>
                  <div>Unidade / Coleções tocadas</div>
                </div>
                {Object.entries(diagAgg.porUsuario).sort(([, a], [, b]) => b.count - a.count).slice(0, 20).map(([nome, d], idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 2fr", gap: "12px", padding: "8px 12px", borderBottom: "1px solid #f8fafc", fontSize: "11px" }}>
                    <div style={{ color: "#1f2937" }}>{nome}</div>
                    <div style={{ textAlign: "right", fontWeight: 700, color: d.count > 5000 ? "#dc2626" : d.count > 1000 ? "#f59e0b" : "#3b82f6" }}>{d.count.toLocaleString()}</div>
                    <div style={{ textAlign: "right", color: "#6b7280" }}>{d.role}</div>
                    <div style={{ color: "#6b7280", overflowWrap: "anywhere" }}>{d.unidade ? `[${d.unidade}] ` : ""}{d.colecoes.join(", ")}</div>
                  </div>
                ))}

                {/* Coleções + eficiência de cache */}
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginTop: "20px", marginBottom: "6px" }}>📋 Coleções por consumo + eficiência de cache</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: "12px", padding: "8px 12px", background: "#f3f4f6", borderRadius: "4px", fontWeight: "600", fontSize: "11px", color: "#6b7280" }}>
                  <div>Coleção</div>
                  <div style={{ textAlign: "right" }}>Chamadas</div>
                  <div style={{ textAlign: "right" }}>Economizadas</div>
                  <div style={{ textAlign: "right" }}>Cache rate</div>
                  <div style={{ textAlign: "right" }}>Erros</div>
                </div>
                {Object.entries(diagAgg.porColecao).sort(([, a], [, b]) => b.count - a.count).slice(0, 20).map(([col, d], idx) => {
                  const totalTentativas = d.count + d.savedReads;
                  const cacheRate = totalTentativas > 0 ? (d.savedReads / totalTentativas) * 100 : 0;
                  return (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: "12px", padding: "8px 12px", borderBottom: "1px solid #f8fafc", fontSize: "11px" }}>
                      <div style={{ color: "#1f2937", fontFamily: "monospace" }}>/{col}</div>
                      <div style={{ textAlign: "right", fontWeight: 700, color: d.count > 5000 ? "#dc2626" : d.count > 1000 ? "#f59e0b" : "#3b82f6" }}>{d.count.toLocaleString()}</div>
                      <div style={{ textAlign: "right", color: "#10b981" }}>{d.savedReads.toLocaleString()}</div>
                      <div style={{ textAlign: "right", fontWeight: 700, color: cacheRate < 30 ? "#dc2626" : cacheRate < 60 ? "#f59e0b" : "#059669" }}>{cacheRate.toFixed(0)}%</div>
                      <div style={{ textAlign: "right", color: d.errors > 0 ? "#dc2626" : "#9ca3af" }}>{d.errors}</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Queries mais lentas */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              🐢 Consultas Mais Lentas
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr",
              gap: "12px",
              marginBottom: "12px",
              fontWeight: "600",
              fontSize: "12px",
              color: "#6b7280",
              padding: "8px 12px",
              background: "#f3f4f6",
              borderRadius: "4px"
            }}>
              <div>Operação</div>
              <div>Coleção</div>
              <div style={{ textAlign: "right" }}>Duração</div>
              <div style={{ textAlign: "right" }}>Tipo</div>
              <div style={{ textAlign: "right" }}>Status</div>
            </div>
            {firestoreMetrics.recentOperations
              .sort((a, b) => (b.duration || 0) - (a.duration || 0))
              .slice(0, 10)
              .map((op, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr",
                    gap: "12px",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f3f4f6",
                    alignItems: "center",
                    fontSize: "13px"
                  }}
                >
                  <div style={{ fontWeight: "600", color: "#1f2937", fontSize: "11px" }}>{op.origin || "—"}</div>
                  <div style={{ color: "#6b7280", fontSize: "11px" }}>{op.collection}</div>
                  <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: "500", color: op.duration > 100 ? "#ef4444" : "#3b82f6" }}>
                    {(op.duration || 0).toFixed(0)}ms
                  </div>
                  <div style={{ textAlign: "right", fontSize: "11px", color: "#6b7280" }}>{op.type || "—"}</div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: op.error ? "#fee2e2" : "#dcfce7",
                      color: op.error ? "#991b1b" : "#166534",
                      fontSize: "11px",
                      fontWeight: "600"
                    }}>
                      {op.error ? "❌" : "✅"}
                    </span>
                  </div>
                </div>
              ))}
          </div>

          {/* TTL Configuração */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              ⏱️ Configuração de TTL
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              gap: "12px",
              marginBottom: "12px",
              fontWeight: "600",
              fontSize: "12px",
              color: "#6b7280",
              padding: "8px 12px",
              background: "#f3f4f6",
              borderRadius: "4px"
            }}>
              <div>Coleção</div>
              <div style={{ textAlign: "right" }}>TTL (min)</div>
              <div style={{ textAlign: "right" }}>Items em Cache</div>
              <div style={{ textAlign: "right" }}>Tamanho</div>
            </div>
            {Object.entries(firestoreMetrics.cache.byCollection || {})
              .map(([coll, data], idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    gap: "12px",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f3f4f6",
                    alignItems: "center",
                    fontSize: "13px"
                  }}
                >
                  <div style={{ fontWeight: "600", color: "#1f2937" }}>{coll}</div>
                  <div style={{ textAlign: "right", color: "#6b7280" }}>
                    {data.ttl ? Math.round(data.ttl / 60000) : "—"}
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: "500", color: "#3b82f6" }}>
                    {data.count}
                  </div>
                  <div style={{ textAlign: "right", color: "#6b7280" }}>
                    {(data.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              ))}
          </div>
        </>
      ) : abaAtiva !== "publica" ? (
        <>
          {/* KPIs — contagem real de ações registradas pelo próprio app
              (não é uma estimativa de leituras/custo do Firebase). */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #3b82f6",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#3b82f6" }}>{dados.length}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📊 Total de Acessos Registrados</div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                {periodo === "hoje" ? "Hoje" : periodo === "semana" ? "Últimos 7 dias" : "Este mês"}
              </div>
            </div>

            <div style={{
              background: "#fff",
              padding: "16px",
              borderRadius: "8px",
              borderLeft: "4px solid #f59e0b",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "28px", fontWeight: "800", color: "#f59e0b" }}>{Object.keys(porUsuario).length}</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>👥 Usuários Ativos</div>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                Média: {(dados.length / (Object.keys(porUsuario).length || 1)).toFixed(1)} ações/usuário
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #3b82f6",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#3b82f6" }}>{totalTentativas.toLocaleString()}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>📊 Total de Tentativas</div>
          </div>

          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #10b981",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#10b981" }}>{tentativasBemsucedidas}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>✅ Consultas Bem-Sucedidas</div>
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
              {totalTentativas > 0 ? ((tentativasBemsucedidas / totalTentativas) * 100).toFixed(1) : 0}% de sucesso
            </div>
          </div>

          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #ef4444",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#ef4444" }}>{tentativasFalhadas}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>❌ Consultas Falhadas</div>
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
              {totalTentativas > 0 ? ((tentativasFalhadas / totalTentativas) * 100).toFixed(1) : 0}% de falha
            </div>
          </div>

          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #f59e0b",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#f59e0b" }}>{Object.keys(porIP).length}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>🔗 IPs Únicos</div>
          </div>

          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #8b5cf6",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "28px", fontWeight: "800", color: "#8b5cf6" }}>{ipsBlockeados.length}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>🚫 IPs Bloqueados</div>
          </div>
        </div>
      )}

      {/* DETALHES POR AÇÃO - Expansível */}
      {abaAtiva !== "publica" && abaAtiva !== "firestore" && (
        <div style={{
          background: "#fff",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          border: "1px solid #e5e7eb"
        }}>
          <div
            onClick={() => setExpandidoAcoes(!expandidoAcoes)}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              fontWeight: "700",
              color: "#1f2937",
              fontSize: "14px"
            }}
          >
            <div>📋 Detalhamento por Ação {expandidoAcoes ? "▼" : "▶"}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400" }}>
              {acoesDados.length} ações · {totalReads.toLocaleString()} registros totais
            </div>
          </div>

          {expandidoAcoes && (
            <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "12px",
                fontWeight: "600",
                fontSize: "12px",
                color: "#6b7280",
                padding: "8px 12px",
                background: "#f3f4f6",
                borderRadius: "4px"
              }}>
                <div>Ação</div>
                <div style={{ textAlign: "right" }}>Registros</div>
                <div style={{ textAlign: "right" }}>% Total</div>
                <div style={{ textAlign: "right" }}>Frequência</div>
              </div>
              {acoesDados.map((acao, idx) => {
                const frequencia = dados.filter(a => a.action === acao.acao).length;
                const mediaReads = frequencia > 0 ? (acao.reads / frequencia).toFixed(0) : 0;
                return (
                  <div
                    key={idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "3fr 1fr 1fr 1fr",
                      gap: "12px",
                      padding: "10px 12px",
                      borderBottom: "1px solid #f3f4f6",
                      alignItems: "center",
                      fontSize: "13px"
                    }}
                  >
                    <div style={{ fontWeight: "600", color: "#1f2937" }}>
                      <span style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        borderRadius: "2px",
                        background: CORES_ACOES[idx % CORES_ACOES.length],
                        marginRight: "8px"
                      }}></span>
                      {acao.acao}
                    </div>
                    <div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: "500", color: "#3b82f6" }}>
                      {acao.reads.toLocaleString()}
                    </div>
                    <div style={{ textAlign: "right", color: "#6b7280" }}>
                      {((acao.reads / totalReads) * 100).toFixed(1)}%
                    </div>
                    <div style={{ textAlign: "right", color: "#6b7280" }}>
                      {frequencia}× ({mediaReads} registros/vez)
                    </div>
                  </div>
                );
              })}

              {/* Insights */}
              <div style={{
                marginTop: "16px",
                padding: "12px",
                background: "#f0f9ff",
                border: "1px solid #bfdbfe",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#1e40af"
              }}>
                <div style={{ fontWeight: "600", marginBottom: "6px" }}>💡 Insights:</div>
                <ul style={{ margin: 0, paddingLeft: "16px" }}>
                  <li>Ação mais frequente: <strong>{acoesDados[0]?.acao}</strong> ({((acoesDados[0]?.reads / totalReads) * 100).toFixed(1)}% do total)</li>
                  <li>Total de registros (período {periodo}): <strong>{totalReads.toLocaleString()}</strong></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtros */}
      {abaAtiva !== "publica" && abaAtiva !== "firestore" ? (
        <div style={{
          background: "#fff",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "24px",
          display: "flex",
          gap: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          <input
            type="text"
            placeholder="Filtrar por usuário..."
            value={filtroUsuario}
            onChange={e => setFiltroUsuario(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px"
            }}
          />
          <select
            value={filtroAcao}
            onChange={e => setFiltroAcao(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              cursor: "pointer"
            }}
          >
            <option value="todos">Todas as ações</option>
            {abaAtiva === "read" ? (
              <>
                <option value="loadRits">Load RITs</option>
                <option value="viewRit">Visualizar RIT</option>
                <option value="consulta_publica">Consulta Pública</option>
              </>
            ) : (
              <>
                <option value="createRit">Criar RIT</option>
                <option value="editRit">Editar RIT</option>
                <option value="deleteRit">Deletar RIT</option>
              </>
            )}
          </select>
        </div>
      ) : (
        <div style={{
          background: "#fff",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "24px",
          display: "flex",
          gap: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          <input
            type="text"
            placeholder="Filtrar por IP..."
            value={filtroIP}
            onChange={e => setFiltroIP(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px"
            }}
          />
          <select
            value={filtroStatus}
            onChange={e => setFiltroStatus(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              cursor: "pointer"
            }}
          >
            <option value="todos">Todos os status</option>
            <option value="sucesso">✅ Bem-Sucedidas</option>
            <option value="bloqueado">🚫 IP Bloqueado</option>
            <option value="rate-limit">⏳ Rate Limited</option>
            <option value="invalido">❌ Código Inválido</option>
          </select>
        </div>
      )}

      {/* Gráficos */}
      {abaAtiva !== "publica" && abaAtiva !== "firestore" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "12px", marginBottom: "24px" }}>
          {/* Top 10 Usuários */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              🏆 Top 10 Usuários
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usuariosDados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="nome" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="reads" name="Registros" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Distribuição por Ação */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              🔄 Distribuição por Ação
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={acoesDados} dataKey="reads" nameKey="acao" cx="50%" cy="50%" label={{ fontSize: 11 }}>
                  {acoesDados.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CORES_ACOES[index % CORES_ACOES.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Por Role */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              👤 Consumo por Perfil
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={roleDados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "12px", marginBottom: "24px" }}>
          {/* Top 10 IPs */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              🔗 Top 10 IPs com Mais Tentativas
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ipsTopDados}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="ip" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Distribuição por Motivo */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              📊 Distribuição de Motivos
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={motivosDados} dataKey="count" nameKey="motivo" cx="50%" cy="50%" label={{ fontSize: 11 }}>
                  {motivosDados.map((entry, index) => {
                    const cores = ["#10b981", "#ef4444", "#f59e0b", "#3b82f6", "#8b5cf6", "#ec4899"];
                    return <Cell key={`cell-${index}`} fill={cores[index % cores.length]} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Proporção Sucesso/Falha */}
          <div style={{
            background: "#fff",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>
              ✅ Taxa de Sucesso
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={[
                  { name: "Bem-Sucedidas", value: tentativasBemsucedidas },
                  { name: "Falhadas", value: tentativasFalhadas }
                ]} dataKey="value" nameKey="name" cx="50%" cy="50%" label={{ fontSize: 11 }}>
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabela Detalhada */}
      {abaAtiva !== "publica" && abaAtiva !== "firestore" ? (
        <div style={{
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          overflow: "hidden"
        }}>
          <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>
            📋 Histórico Detalhado
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>👤 Usuário</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Perfil</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>🔄 Ação</th>
                  <th style={{ padding: "12px", textAlign: "right", color: "#6b7280", fontWeight: "600" }}>📊 Registros</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>⏰ Horário</th>
                </tr>
              </thead>
              <tbody>
                {dados.slice(0, 50).map((log, idx) => (
                  <tr key={log.id} style={{
                    background: idx % 2 === 0 ? "#f9fafb" : "#fff",
                    borderBottom: "1px solid #e5e7eb"
                  }}>
                    <td style={{ padding: "12px", color: "#1f2937", fontWeight: "500" }}>{log.userName}</td>
                    <td style={{ padding: "12px" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        background: ROLE_COLORS[log.userRole] + "15",
                        color: ROLE_COLORS[log.userRole],
                        fontSize: "11px",
                        fontWeight: "600"
                      }}>
                        {log.userRole}
                      </span>
                    </td>
                    <td style={{ padding: "12px", color: "#6b7280" }}>{log.action}</td>
                    <td style={{ padding: "12px", textAlign: "right", color: "#3b82f6", fontWeight: "600" }}>
                      {log.estimatedReads}
                    </td>
                    <td style={{ padding: "12px", color: "#6b7280", fontSize: "11px" }}>
                      {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString('pt-BR') : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          overflow: "hidden"
        }}>
          <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>
            📋 Histórico de Tentativas de Consulta Pública
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>🔗 IP</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>📄 RIT Consultado</th>
                  <th style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}>Status</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>📋 Motivo</th>
                  <th style={{ padding: "12px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>⏰ Horário</th>
                </tr>
              </thead>
              <tbody>
                {consultasFiltradas.slice(0, 100).map((log, idx) => (
                  <tr key={log.id} style={{
                    background: idx % 2 === 0 ? "#f9fafb" : "#fff",
                    borderBottom: "1px solid #e5e7eb"
                  }}>
                    <td style={{ padding: "12px", color: "#1f2937", fontWeight: "500", fontFamily: "monospace" }}>{log.ip}</td>
                    <td style={{ padding: "12px", color: "#6b7280" }}>{log.numero_rit || "—"}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        background: log.sucesso ? "#dcfce7" : "#fee2e2",
                        color: log.sucesso ? "#166534" : "#991b1b",
                        fontSize: "11px",
                        fontWeight: "600"
                      }}>
                        {log.sucesso ? "✅ Sucesso" : "❌ Falha"}
                      </span>
                    </td>
                    <td style={{ padding: "12px", color: "#6b7280", fontSize: "11px" }}>{log.motivo || "—"}</td>
                    <td style={{ padding: "12px", color: "#6b7280", fontSize: "11px" }}>
                      {log.timestamp ? new Date(log.timestamp.seconds * 1000).toLocaleString('pt-BR') : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
